import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { tenantsApi } from "@/api/client";
import type { CreateTenantRequest, PlanSize } from "@/types";

const TENANT_ID_REGEX = /^[a-z][a-z0-9-]{2,30}$/;
const PLANS: { value: PlanSize; label: string; spec: string }[] = [
  { value: "small", label: "Small", spec: "1 vCPU / 2 GB · 1 task · ≤ 100 users" },
  { value: "medium", label: "Medium", spec: "2 vCPU / 4 GB · 2 tasks · ≤ 300 users" },
  { value: "large", label: "Large", spec: "4 vCPU / 8 GB · 3 tasks · ≤ 1000 users" },
];

export default function CreateTenant() {
  const navigate = useNavigate();

  const [form, setForm] = useState<CreateTenantRequest>({
    tenantId: "",
    displayName: "",
    adminEmail: "",
    planSize: "small",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function update<K extends keyof CreateTenantRequest>(key: K, value: CreateTenantRequest[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): string | null {
    if (!TENANT_ID_REGEX.test(form.tenantId)) {
      return "tenantId must match ^[a-z][a-z0-9-]{2,30}$";
    }
    if (form.displayName.length === 0 || form.displayName.length > 100) {
      return "displayName must be 1-100 chars";
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.adminEmail)) {
      return "adminEmail is not a valid email";
    }
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setBusy(true);
    try {
      const res = await tenantsApi.create(form);
      navigate(`/tenants/${res.tenantId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <h1>Add Workspace</h1>
      <p className="muted">
        Provisioning takes about 5–10 minutes. Estimated cost ≈ <strong>$57/month</strong> per Small plan.
      </p>

      <form onSubmit={onSubmit}>
        <label>Tenant slug</label>
        <input
          type="text"
          required
          placeholder="acme"
          pattern="[a-z][a-z0-9-]{2,30}"
          value={form.tenantId}
          onChange={(e) => update("tenantId", e.target.value.trim())}
        />
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Used as subdomain: <code>{form.tenantId || "your-slug"}.mm.example.com</code>
        </p>

        <div className="spacer" />
        <label>Display name</label>
        <input
          type="text"
          required
          placeholder="ACME Corporation"
          maxLength={100}
          value={form.displayName}
          onChange={(e) => update("displayName", e.target.value)}
        />

        <div className="spacer" />
        <label>Admin email</label>
        <input
          type="email"
          required
          placeholder="admin@acme.com"
          value={form.adminEmail}
          onChange={(e) => update("adminEmail", e.target.value.trim())}
        />
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Initial workspace admin will be created with this email.
        </p>

        <div className="spacer" />
        <label>Plan</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 480 }}>
          {PLANS.map((p) => (
            <label key={p.value} style={{ display: "flex", gap: 12, padding: 12, border: "1px solid var(--color-border)", borderRadius: 8, cursor: "pointer", background: form.planSize === p.value ? "#eff6ff" : "white" }}>
              <input
                type="radio"
                name="plan"
                value={p.value}
                checked={form.planSize === p.value}
                onChange={() => update("planSize", p.value)}
              />
              <div>
                <strong>{p.label}</strong>
                <div className="muted" style={{ fontSize: 13 }}>{p.spec}</div>
              </div>
            </label>
          ))}
        </div>

        {error && <p className="error">{error}</p>}

        <div className="spacer" />
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => navigate(-1)} disabled={busy}>Cancel</button>
          <button type="submit" className="primary" disabled={busy}>
            {busy ? "Creating..." : "Create workspace"}
          </button>
        </div>
      </form>
    </div>
  );
}
